import React from 'react';
import {
  OrganizationHeader,
  OrganizationTitle,
  OrganizationBody,
  OrganizationButtonTh,
  OrganizationTdButton,
  OrganizationTable,
  OrganizationTh,
  OrganizationTd
} from '@this/components/organization/organization.style';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import { styled } from '@this/constants/themes';
import Button from '@this/shared/atoms/button';
import Context from '@this/components/core_app/core_app_context';
import { observer } from 'mobx-react';
import { PaymentEditForm } from '@this/components/organization/payment/payment_edit_form';
import type { PaymentGatewayUser } from '@this/domain/organization/payment_gateway_user';
import { PaymentSetting } from '@this/domain/organization/payment_setting';
import { DeleteMasterButton } from '@this/components/organization/delete-master-button';
import Notification from '@this/src/notification';
import { Fetcher, HTTPError, trackEvent } from '@this/src/util';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type {
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
  DropResult
} from 'react-beautiful-dnd';
import { Alert } from '@this/shared/ui/feedbacks/alert';

interface Props {}

interface State {
  loading: boolean;
  showModal: boolean;
  setting: PaymentSetting | null;
  targetPaymentGatewayUser: PaymentGatewayUser | null;
  saving: boolean;
}

interface PaymentSettingResponse {
  payment_setting: any;
}

@observer
class Payment extends React.Component<Props, State> {
  static contextType = Context;

  context!: React.ContextType<typeof Context>;

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      showModal: false,
      targetPaymentGatewayUser: null,
      setting: null,
      saving: false
    };
  }

  setSonyPaymentToken = (token: string, maskedCreditCard: string) => {
    const params = { token, maskedCreditCard };
    Fetcher.post<Response>('/organization/payment/add_credit_card.json', params)
      .then(() => {
        trackEvent('Payment succeed');
        location.reload();
      })
      .catch(e => {
        if (e instanceof HTTPError && e.response?.status === 400) {
          Notification.error(e.response.data.errors);
        }
        window.setTimeout(() => {
          location.reload();
        }, 5000);
      });
  };

  handleAddCreditCard() {
    SpsvApi.spsvCardInfoScreen();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await this.fetchPaymentSetting();

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.state?.setting?.tokenGenerateScriptUrl || '';
    script.setAttribute('callBackFunc', 'setSonyPaymentToken');
    script.className = 'spsvToken';
    document.head.appendChild(script);

    window.setSonyPaymentToken = this.setSonyPaymentToken;

    this.setState({ loading: false });
  }

  componentWillUnmount() {
    window.setSonyPaymentToken = null;
  }

  async fetchPaymentSetting() {
    await Fetcher.get<PaymentSettingResponse>('/organization/payment.json').then(result => {
      const paymentSetting = new PaymentSetting(result.payment_setting);
      this.setState({ setting: paymentSetting });
    });
  }

  async updatePaymentGatewayUser(paymentGatewayUser: PaymentGatewayUser) {
    this.setState({ saving: true });
    try {
      await Fetcher.put(`/organization/payment`, paymentGatewayUser.submitParams());
      await this.fetchPaymentSetting();
    } finally {
      this.setState({ saving: false, showModal: false });
    }
  }

  handleEditClick(paymentGatewayUser: PaymentGatewayUser) {
    this.setState({ showModal: true, targetPaymentGatewayUser: paymentGatewayUser });
  }

  handleAbortPaymentEdit() {
    this.setState({ showModal: false });
  }

  async handleDragEnd(result: DropResult) {
    const { destination, source } = result;
    const { setting } = this.state;
    if (!setting || !destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const members = [...setting.gatewayMembers];
    const [moved] = members.splice(source.index, 1);
    members.splice(destination.index, 0, moved);
    setting.gatewayMembers = members;
    this.setState({ setting, saving: true });

    try {
      await Fetcher.put(`/organization/payment/reorder`, { ids: members.map(member => member.id) });
      await this.fetchPaymentSetting();
    } catch (e) {
      if (e instanceof HTTPError && e.response?.status === 400) {
        Notification.error(e.response.data.errors);
      } else {
        Notification.error('優先順位の保存に失敗しました。時間をおいてもう一度お試しください。');
      }
      await this.fetchPaymentSetting();
    } finally {
      this.setState({ saving: false });
    }
  }

  async handleDeleteGatewayUserButtonClick(paymentGatewayUser: PaymentGatewayUser) {
    this.setState({ saving: true });
    try {
      await Fetcher.delete(`/organization/payment`, { id: paymentGatewayUser.id });

      location.reload();
    } finally {
      this.setState({ saving: false });
    }
  }

  render() {
    const { loading, showModal, setting, targetPaymentGatewayUser, saving } = this.state;
    // const params = useParams();
    return (
      <>
        <OrganizationTitle>支払い設定</OrganizationTitle>
        <OrganizationHeader>
          <TheButton onClick={() => this.handleAddCreditCard()}>クレジットカード追加</TheButton>
        </OrganizationHeader>
        <OrganizationBody>
          {loading ? (
            <SimpleLoading />
          ) : (
            <>
              <Alert variant="info" style={{ marginBottom: '16px' }}>
                上から順に決済します。行をドラッグして優先順位を並べ替えられます。
              </Alert>
              <DragDropContext onDragEnd={result => this.handleDragEnd(result)}>
                <OrganizationTable>
                  <thead>
                    <tr>
                      <OrganizationTh>優先順位</OrganizationTh>
                      <OrganizationTh>名前</OrganizationTh>
                      <OrganizationTh>カード番号</OrganizationTh>
                      <OrganizationButtonTh>編集</OrganizationButtonTh>
                      <OrganizationButtonTh>削除</OrganizationButtonTh>
                    </tr>
                  </thead>
                  <Droppable droppableId="paymentGatewayMembers">
                    {(provided: DroppableProvided) => (
                      <tbody {...provided.droppableProps} ref={provided.innerRef}>
                        {setting?.gatewayMembers.map((member, i) => (
                          <Draggable draggableId={String(member.id)} index={i} key={member.id}>
                            {(dragProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                              <tr
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                style={{
                                  background: snapshot.isDragging ? '#fff9ed' : undefined,
                                  ...dragProvided.draggableProps.style
                                }}
                              >
                                <OrganizationTd>{i + 1}</OrganizationTd>
                                <OrganizationTd>{member.name}</OrganizationTd>
                                <OrganizationTd>{member.maskedCreditCardNumber}</OrganizationTd>
                                <OrganizationTd>
                                  <OrganizationTdButton onClick={() => this.handleEditClick(member)}>
                                    編集
                                  </OrganizationTdButton>
                                </OrganizationTd>
                                <OrganizationTd>
                                  <DeleteMasterButton
                                    targetLabel={member.name}
                                    onConfirm={() => {
                                      this.handleDeleteGatewayUserButtonClick(member);
                                    }}
                                  />
                                </OrganizationTd>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </OrganizationTable>
              </DragDropContext>
            </>
          )}
        </OrganizationBody>

        {targetPaymentGatewayUser && (
          <PaymentEditForm
            open={showModal}
            paymentGatewayUser={targetPaymentGatewayUser}
            submitting={saving}
            onAbort={() => this.handleAbortPaymentEdit()}
            onSubmit={targetPaymentGatewayUser => this.updatePaymentGatewayUser(targetPaymentGatewayUser)}
          />
        )}
      </>
    );
  }
}

const TheButton = styled(Button)`
  margin-right: auto;
  padding: 3px 20px;
`;

export default Payment;
